'use client';

import { useMemo, useEffect } from 'react';
import Image from 'next/image';
import BigNumber from 'bignumber.js';
import { useWriteContract } from '@ywwwtseng/wallet-kit/evm';
import { useWalletKitConnect } from '@ywwwtseng/wallet-kit';
import { toast, useI18n, Button, useNavigate, useMutation, displayAmount } from '@ywwwtseng/react-kit';
import { Typography } from '@ywwwtseng/tma-react-kit';
import { useInvestmentTeamLock } from '@/hooks/useInvestmentTeamLock';
import { MOONINI_LOCK_ADDRESS, MOONINI_DECIMALS } from '@/constants';
import MOONINI_LOCK_ABI from '@/assets/abi/MooniniLock.abi.json';
import * as web3 from '@ywwwtseng/web3';
import { useInvestments } from '@/hooks/useInvestments';
import { type FormElement, Form, validator, useForm } from '@/lib/react-form';

export default function InvestmentScreen({
  params,
}: {
  params: { index: number };
}) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const walletKitConnect = useWalletKitConnect();
  const bscAddress = walletKitConnect.accounts.bsc.address;
  const { investments } = useInvestments(bscAddress);

  const investment = useMemo(() => {
    if (!investments || investments.length <= params.index) return null;
    return investments[params.index];
  }, [investments, params.index]);
  const isTeamLock = useInvestmentTeamLock(investment);

  // 转移表单
  const form = useForm({
    values: {
      default: {
        assignee: '',
        amount: '',
      },
    },
  });

  // Mutation 用于记录 right assignment
  const { mutate: createRightAssignment, isLoading: isCreatingRecord } = useMutation('right_assignments:create');

  // 转移
  const { writeContract: rightAssign, isLoading: isAssigning } = useWriteContract({
    onSuccess: async (receipt: { hash?: string; transactionHash?: string }) => {
      const transactionHash = (receipt as { hash?: string; transactionHash?: string }).hash ||
        (receipt as { hash?: string; transactionHash?: string }).transactionHash;

      // 调用 mutation 记录到数据库
      await createRightAssignment({
        tx_hash: transactionHash,
      });

      navigate(-1);
    },
    onError: (error: Error) => {
      toast.error(JSON.stringify(error));
    },
  });

  const handleTransfer = async () => {
    if (!MOONINI_LOCK_ADDRESS || !bscAddress) return;

    if (!web3.ethers.isAddress(form.values.assignee)) {
      toast.error(t('investor.invalid_address'));
      return;
    }

    const amount = web3.utils.parseUnits(form.values.amount as string, MOONINI_DECIMALS);

    await rightAssign({
      address: MOONINI_LOCK_ADDRESS,
      abi: MOONINI_LOCK_ABI,
      functionName: 'rightAssign',
      args: [form.values.assignee, amount, params.index],
    });
  };

  const remaining = investment
    ? new BigNumber(investment.totalClaimableAmount).minus(new BigNumber(investment.claimedAmount))
    : new BigNumber(0);
  const maxAmount = web3.utils.formatUnits(remaining.toFixed(), MOONINI_DECIMALS);
  const formattedRemaining = web3.utils.formatUnits(remaining.toFixed(), MOONINI_DECIMALS);

  const elements = useMemo<{ row: FormElement[] }[]>(() => {


    return [
      {
        row: [
          {
            ui: 'text',
            direction: 'column',
            label: t('investor.assignee_placeholder'),
            name: 'assignee',
            validate: [
              validator.required({
                message: t('investor.invalid_input') || 'Please enter assignee address',
              }),
              (value: unknown) => {
                if (typeof value === 'string' && value.trim() !== '' && !web3.ethers.isAddress(value)) {
                  return t('investor.invalid_address') || 'Invalid address';
                }
              },
            ],
            props: {
              placeholder: '0x...',
            },
          },
        ],
      },
      {
        row: [
          {
            ui: 'amount',
            direction: 'column',
            label: t('investor.amount_placeholder') || 'Amount',
            name: 'amount',
            validate: [
              validator.required({
                message: t('investor.invalid_input') || 'Please enter amount',
              }),
              validator.gt('0', {
                message: t('investor.invalid_input') || 'Amount must be greater than 0',
              }),
              validator.lte(maxAmount, {
                message: t('investor.invalid_input') || `Amount cannot exceed ${maxAmount} MOONINI`,
              }),
            ],
            props: {
              placeholder: '0',
            },
          },
        ],
      },
    ];
  }, [investment, params.index, t]);

  useEffect(() => {
    void walletKitConnect.switchNetwork(process.env.NEXT_PUBLIC_APP_ENV === 'production' ? 'bsc' : 'bscTestnet');
  }, []);

  if (!bscAddress) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full gap-4 py-3 px-5">
        <Button
          size="sm"
          rounded="full"
          variant="contained"
          color="primary"
          onClick={() => void walletKitConnect.open()}
        >
          {t('investor.connect_wallet') || 'Connect Wallet'}
        </Button>
        <Typography size="2" color="rgba(255,255,255,0.54)" className="text-center">
          {t('investor.connect_wallet_hint') || 'Please connect your BSC wallet to view investment information'}
        </Typography>
      </div>
    );
  }

  if (!investment) {
    return (
      null
    );
  }

  const percentage = form.values.amount ? new BigNumber(form.values.amount as string).div(maxAmount).toNumber() * 100 : 0;

  return (
    <div className="flex flex-col justify-start w-full h-full gap-2 py-3 px-5">
      <div className="v-stack w-full gap-2 pb-2 border-b border-border">
        <Typography size="2" color="rgba(255,255,255,0.54)">
          {t(isTeamLock ? 'investor.team_lock' : 'investor.investment')} #{params.index + 1}
        </Typography>
        <div className="h-stack items-center gap-2">
          <Typography size="2" color="rgba(255,255,255,0.54)">
            {t('investor.remaining')}:
          </Typography>
          <div className="h-stack items-center gap-1">
            <Typography size="2" weight={500} className="text-primary">
              {formattedRemaining}
            </Typography>
            <Image src="/images/tokens/moonini.png" alt="moonini" width={16} height={16} />
            <Typography size="2" color="rgba(255,255,255,0.54)">
              $MOONINI
            </Typography>
          </div>
        </div>
      </div>
      <Form
        name="investment_transfer"
        isLoading={isAssigning || isCreatingRecord}
        {...form}
        elements={elements}
        confirm={({ isLoading, onConfirm }) => (
          <>
            <Typography size="2">
              {t('investor.transfer_confirm')}
            </Typography>
            <div className="w-full flex flex-row items-center justify-between px-4">
              <Typography size="1">
                To
              </Typography>
              <Typography size="1">
                {`${form.values.assignee}`}
              </Typography>
            </div>
            <div className="w-full flex flex-row items-center justify-between px-4">
              <Typography size="1">
                {`${t('investor.transfer')}`}
              </Typography>
              <Typography size="1">
                {percentage > 0 ? `${form.values.amount} $MOONINI (${displayAmount(percentage, { showTinyAmount: false })}%)` : `${form.values.amount} $MOONINI`}
              </Typography>
            </div>
            <div className="w-full px-4">
              <Button
                isLoading={isLoading}
                size="sm"
                rounded="full"
                width="full"
                variant="contained"
                color="primary"
                onClick={onConfirm}
              >
                {t('investor.transfer_button')}
              </Button>
            </div>
          </>
        )}
        submit={{
          label: t('investor.transfer_button'),
        }}
        onSubmit={handleTransfer}
      />
    </div>
  );
}
